import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import type { OptionsType } from '@atlaskit/select/types';
import type { AsyncFieldConfiguration } from '@atlassian/proforma-form-renderer/async-fields';
import type { FormOptionsType } from '@atlassian/proforma-form-renderer/form-fields';

import { useUsersService } from '../../../../../services/users';
import { messages } from './messages';

export const useUsers = () => {
	di(useUsersService);
	const { fetch } = useUsersService();
	const { formatMessage } = useIntl();

	return useCallback(
		(): AsyncFieldConfiguration => ({
			// Errors thrown here are silently caught by the FormRenderer's select components,
			// which call .catch(() => callback([])) — a failed fetch renders as an empty dropdown.
			// See: platform/packages/proforma/proforma-form-renderer/src/ui/form-renderer/form-sections/form-section/form-extension-renderer/form-field-renderer/single-select-input/edit/index.tsx L182-186
			// and the equivalent multiple-select-input/edit/index.tsx L182-186.
			loadOptions: async (query) => {
				const users = await fetch(query);
				return users.map<OptionsType<FormOptionsType>[number]>((user) => ({
					value: user.accountId,
					label: user.displayName ?? '',
				}));
			},
			cacheOptions: true,
			noOptionsMessage: (input?: { inputValue?: string }) => {
				if (input?.inputValue) {
					return formatMessage(messages.noOptions);
				}
				return formatMessage(messages.startTyping);
			},
			defaultOptions: false,
		}),
		[fetch, formatMessage],
	);
};
